import { Component, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { AutomotiveBusinessLevel } from "@enums/automotiveBusinessLevel.enum";
import { PpapCapability } from "@enums/ppapCapability.enum";
import { PurchaseTypeEnum } from "@enums/purchaseType.enum";
import { YesNoConsidering } from "@enums/yesNoConsidering.enum";
import { YesNoNa } from "@enums/yesNoNa.enum";
import { CheckGroupPermissions } from "@helpers/checkGroupPermissions";
import { GetPropertiesFromEnum } from "@helpers/getPropertiesFromEnum";
import { AdminHeaderButton } from "@interfaces/adminHeaderButton.interface";
import { Contract } from "@interfaces/models/contract.interface";
import { PaymentTerm } from "@interfaces/models/paymentTerm.interface";
import { PurchaseLog } from "@interfaces/models/purchaseLog.interface";
import { Supplier } from "@interfaces/models/supplier.interface";
import { SupplierQuestionnaire } from "@interfaces/models/supplierQuestionnaire.interface";
import { SupplierQuestionnaireApproval } from "@interfaces/models/supplierQuestionnaireApproval.interface";
import { SelectablePurchaseType } from "@interfaces/selectablePurchaseType.interface";
import { SupplierQuestionnaireContactLineData } from "@interfaces/supplierQuestionnaireContactLineData.interface";
import { SupplierContact } from "@interfaces/supplierContact.interface";
import { SupplierDocument } from "@interfaces/supplierDocument.interface";
import { SupplierLocation } from "@interfaces/supplierLocation.interface";
import { SupplierPurchaseType } from "@interfaces/supplierPurchaseType.interface";
import { AppSettingsService } from "@services/appSettings.service";
import { ContractService } from "@services/contract.service";
import { FileService } from "@services/file.service";
import { PaymentTermService } from "@services/paymentTerm.service";
import { PurchaseLogService } from "@services/purchaseLog.service";
import { SupplierService } from "@services/supplier.service";
import { SupplierContactService } from "@services/supplierContact.service";
import { SupplierDocumentService } from "@services/supplierDocument.service";
import { SupplierLocationService } from "@services/supplierLocation.service";
import { SupplierPurchaseTypeService } from "@services/supplierPurchaseType.service";
import { SupplierQuestionnaireService } from "@services/supplierQuestionnaire.service";
import { ToastService } from "angular-toastify";
import { DxFormComponent } from "devextreme-angular";

@Component({
	moduleId: module.id,
	selector: "suppliers-id",
	styleUrls: ["suppliers-id.css"],
	templateUrl: "suppliers-id.html"
})
export class SupplierIdAdminComponent {
	@ViewChild("supplierForm", { static: false }) form!: DxFormComponent;
	approvalComment = "";
	automotiveBusinessLevelOptions = [
		{ id: AutomotiveBusinessLevel.ZeroToFifty, name: "0-50%" },
		{ id: AutomotiveBusinessLevel.FiftyToSeventyFive, name: "50-75%" },
		{ id: AutomotiveBusinessLevel.SeventyFiveToOneHundred, name: "75-100%" },
	];
	canApproveQuestionnaire = false;
	contacts: SupplierContact[] = [];
	contracts: Contract[] = [];
	createEnabled = true;
	documents: SupplierDocument[] = [];
	editEnabled = true;
	headerPrimaryButtons: AdminHeaderButton[] = [];
	headerTertiaryButtons: AdminHeaderButton[] = [];
	isPurchaseManager = false;
	isSuperAdmin = false;
	item: Supplier = new Supplier();
	itemId = 0;
	itemType = "Supplier";
	locations: SupplierLocation[] = [];
	paymentTerms: PaymentTerm[] = [];
	popupAddButtonOptions: any = { onClick: () => this.addNewContact(), text: "Add Contact" };
	popupCloseButtonOptions: any = { onClick: () => this.closePopup(), text: "Close" };
	popupContact: SupplierContact = new SupplierContact();
	popupLocation: SupplierLocation = new SupplierLocation();
	popupSupplierDocument: SupplierDocument = new SupplierDocument();
	popupTitle = "Add New Contact";
	popupType = "";
	popupVisible = false;
	ppapOptions = [
		{ id: PpapCapability.Yes, name: "Yes" },
		{ id: PpapCapability.No, name: "No" },
		{ id: PpapCapability.RequireHelp, name: "Require Help" },
	];
	purchaseLogsDueLastThirtyDays: PurchaseLog[] = [];
	purchaseLogsOverdue: PurchaseLog[] = [];
	purchaseLogsPaymentDueNextThirtyDays: PurchaseLog[] = [];
	purchaseTypes: any;
	questionnaire: SupplierQuestionnaire | null = null;
	questionnaireApprovals: SupplierQuestionnaireApproval[] = [];
	questionnaireContacts: SupplierQuestionnaireContactLineData[] = [];
	questionnaireLinkUrl = "";
	questionnairePopupMode = "send"; // "send" or "reset" — drives the popup wording and action
	questionnaireRecipientEmail = "";
	readOnly = true;
	returnUrl: string = location.href.split("/")[3] + "/" + location.href.split("/")[4];
	selectablePurchaseTypes: SelectablePurchaseType[] = [];
	spendSummary: any;
	statuses: any;
	supplierPurchaseTypes: SupplierPurchaseType[] = [];
	title: string = "View " + this.itemType;
	uploadEnabled = false;
	yesNoConsideringOptions = [
		{ id: YesNoConsidering.Yes, name: "Yes" },
		{ id: YesNoConsidering.No, name: "No" },
		{ id: YesNoConsidering.Considering, name: "Considering" },
	];
	yesNoNaOptions = [
		{ id: YesNoNa.Yes, name: "Yes" },
		{ id: YesNoNa.No, name: "No" },
		{ id: YesNoNa.NotApplicable, name: "N/A" },
	];

	constructor(private appSettingsService: AppSettingsService, private router: Router, private toastService: ToastService, private route: ActivatedRoute, private contractService: ContractService, private fileService: FileService, private paymentTermsService: PaymentTermService, private purchaseLogService: PurchaseLogService, private itemService: SupplierService, private supplierContactsService: SupplierContactService, private supplierDocumentService: SupplierDocumentService, private supplierLocationService: SupplierLocationService, private supplierPurchaseTypeService: SupplierPurchaseTypeService, private supplierQuestionnaireService: SupplierQuestionnaireService) {
		this.buildButtons("view");

		this.route.params.subscribe((params) => {
			if (params.id != "create") {
				if (params.id.split("_").length > 1) {
					this.itemId = params.id.split("_")[0];
					this.getItem(this.itemId);
					this.edit();
				} else {
					this.itemId = params.id;
					this.getItem(params.id);
				}
			} else {
				this.create();
			}
		});

		this.getPaymentTerms();

		this.downloadDocument = this.downloadDocument.bind(this);
		this.deleteContact = this.deleteContact.bind(this);
		this.editContactPopup = this.editContactPopup.bind(this);
		this.editLocationPopup = this.editLocationPopup.bind(this);
		this.setPrimaryBilling = this.setPrimaryBilling.bind(this);
		this.setPrimaryDelivery = this.setPrimaryDelivery.bind(this);
		this.togglePurchaseType = this.togglePurchaseType.bind(this);
		this.uploadFile = this.uploadFile.bind(this);
		this.viewContractLink = this.viewContractLink.bind(this);
		this.viewPurchaseLogLink = this.viewPurchaseLogLink.bind(this);
		
		this.isPurchaseManager = CheckGroupPermissions(appSettingsService, "Purchase Manager");
		// Super Admin can override the questionnaire-driven flags by hand; everyone else sees them read-only.
		this.isSuperAdmin = CheckGroupPermissions(appSettingsService, "Super Admin");
		// Mirrors the roles on SupplierQuestionnaireController.Approve
		this.canApproveQuestionnaire = CheckGroupPermissions(appSettingsService, "Super Admin")
			|| CheckGroupPermissions(appSettingsService, "Purchase Manager")
			|| CheckGroupPermissions(appSettingsService, "Contract Management");
	}

	addDocumentPopup() {
		this.popupVisible = true;
		this.popupAddButtonOptions = { onClick: () => this.createSupplierDocument(), text: "Add Document" };
		this.popupTitle = "Add Document";
		this.popupType = "Document";
	}

	addNewContact() {
		this.popupContact.supplierId = this.item.id;
		this.supplierContactsService.createSingle(this.popupContact)
			.subscribe(
				() => {
					this.closePopup();
					this.getContacts();
				},
				(err: any) => console.log(err)
			);
	}

	addNewContactPopup() {
		this.popupVisible = true;
		this.popupAddButtonOptions = { onClick: () => this.addNewContact(), text: "Add Contact" };
		this.popupContact = new SupplierContact();
		this.popupTitle = "Add New Contact";
		this.popupType = "Contact";
	}

	addNewLocation() {
		this.popupLocation.supplierId = this.item.id;
		this.supplierLocationService.createSingle(this.popupLocation)
			.subscribe(
				() => {
					this.closePopup();
					this.getLocations();
				},
				(err: any) => console.log(err)
			);
	}

	addNewLocationPopup() {
		this.popupVisible = true;
		this.popupAddButtonOptions = { onClick: () => this.addNewLocation(), text: "Add Location" };
		this.popupLocation = new SupplierLocation();
		this.popupTitle = "Add New Location";
		this.popupType = "Location";
	}

	/**
	 * Reset & Re-send only appears once there is something to reset — a questionnaire the supplier has
	 * actually completed — and only for the groups who can approve, since resetting un-approves a supplier.
	 * Called again after the questionnaire loads, because buildButtons runs before it arrives.
	 */
	addResetQuestionnaireButton() {
		const completed = this.questionnaire != null && this.questionnaire.submittedOn != null;
		if (completed === false || this.canApproveQuestionnaire === false) { return; }
		if (this.headerTertiaryButtons.some(b => b.method === "resetQuestionnairePopup")) { return; }
		if (this.headerTertiaryButtons.length === 0) { return; } // create mode has no tertiary buttons

		this.headerTertiaryButtons = [...this.headerTertiaryButtons, { method: "resetQuestionnairePopup", text: "Reset & Re-send Questionnaire" }];
	}

	approveQuestionnaire() {
		if (this.questionnaire == null) { return; }

		if (confirm("Are you sure you wish to approve this supplier questionnaire? Two different approvers are required before the supplier is approved.") === false) {
			return;
		}

		this.supplierQuestionnaireService.approve(this.questionnaire.id, this.approvalComment)
			.subscribe(
				() => {
					this.approvalComment = "";
					this.toastService.success("Questionnaire approved.");
					this.getQuestionnaire();
					this.getItem(this.itemId); // ApprovedSupplier may have just been set by the second approval
				},
				(err: any) => {
					console.log(err);
					this.toastService.error(err?.error?.response?.error || "Could not approve this questionnaire.");
				}
			);
	}

	boolYesNo(cellInfo: any){
		if(cellInfo.value === true)
		{
			return "YES";
		}
		else
		{
			return "NO";
		}
	}

	buildButtons(mode: string) {
		if (mode == "edit" && this.editEnabled) {
			this.headerPrimaryButtons = [
				{ method: "updateItem", text: "Save Changes" },
				{ method: "cancelEditing", text: "View Mode" },
				{ method: "close", text: "Close" }
			];
			this.headerTertiaryButtons = [
				{ method: "addNewContactPopup", text: "Add New Contact" },
				{ method: "addDocumentPopup", text: "Add Document" },
				{ method: "addNewLocationPopup", text: "Add Location" },
				{ method: "sendQuestionnairePopup", text: "Send Questionnaire" }
			];
			this.addResetQuestionnaireButton();
		} else if (mode == "create" && this.createEnabled) {
			this.headerPrimaryButtons = [
				{ method: "createItem", text: "Save" },
				{ method: "close", text: "Close" }
			];
			this.headerTertiaryButtons = [];
		} else if (mode == "view") {
			this.headerPrimaryButtons = [
				{ method: "edit", text: "Edit" },
				{ method: "close", text: "Close" }
			];
			this.headerTertiaryButtons = [
				{ method: "addNewContactPopup", text: "Add New Contact" },
				{ method: "addDocumentPopup", text: "Add Document" },
				{ method: "addNewLocationPopup", text: "Add Location" },
				{ method: "sendQuestionnairePopup", text: "Send Questionnaire" }
			];
			this.addResetQuestionnaireButton();	
		}
	}

	cancelEditing() {
		this.uploadEnabled = false;
		this.readOnly = true;
		this.buildButtons("view");
		this.title = "View " + this.itemType;
	}

	close() {
		this.router.navigate([`/${this.returnUrl}/`]);
	}
	
	closePopup() {
		this.popupVisible = false;
	}

	copyQuestionnaireLink() {
		const link = this.questionnaireLinkUrl;
		if (!link) { return; }
		if (navigator.clipboard != null) {
			navigator.clipboard.writeText(link).then(
				() => this.toastService.success("Questionnaire link copied to clipboard."),
				() => this.toastService.warn("Couldn't copy automatically — select the link and copy it manually.")
			);
		} else {
			this.toastService.warn("Clipboard not available — select the link and copy it manually.");
		}
	}

	create() {
		this.uploadEnabled = false;
		this.readOnly = false;
		this.buildButtons("create");
		this.title = "Create New " + this.itemType;
	}

	createItem() {
		this.itemService.createSingle(this.item)
			.subscribe(
				() => {
					this.toastService.success("Successfully Updated " + this.itemType);
					this.item = new Supplier();
					this.close();
				},
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				}
			);
	}

	createSupplierDocument() {
		this.popupSupplierDocument.supplierId = this.itemId;
		this.supplierDocumentService.createSingle(this.popupSupplierDocument)
			.subscribe(
				() => {
					this.toastService.success("Successfully Added New Document " + this.popupSupplierDocument.fileName);
					this.popupSupplierDocument = new SupplierDocument();
					this.getDocuments();
					this.popupVisible = false;
				},
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				}
			);
	}

	deleteContact(e: any) {
		if (confirm("Are you sure you wish to delete this contact? Note: Contacts attached to Purchase Logs cannot be deleted.") == false) {
			return;
		}

		this.supplierContactsService.deleteSingleById(e.row.data.id).subscribe(
			() => {
				this.toastService.success("Successfully Deleted Contact");
				this.getContacts();
			},
			(err) => {
				this.toastService.error("Could not delete contact - " + err);
			}
		);
	}

	deleteLocation(e: any) {
		if (confirm("Are you sure you wish to delete this location?") == false) {
			return;
		}

		this.supplierLocationService.deleteSingleById(e.row.data.id).subscribe(
			() => {
				this.toastService.success("Successfully Deleted Location");
				this.getLocations();
			},
			(err) => {
				this.toastService.error("Could not delete Location - " + err);
			}
		);
	}

	downloadDocument(e: any) {
		this.fileService.downloadFile(e.row.data.fileLookupId).subscribe(
			(response: any) =>{
				const dataType = response.type;
				const binaryData = [];
				binaryData.push(response);
				const downloadLink = document.createElement("a");
				downloadLink.href = window.URL.createObjectURL(new Blob(binaryData, {type: dataType}));
				downloadLink.setAttribute("download", e.row.data.fileName);
				document.body.appendChild(downloadLink);
				downloadLink.click();
			}
		);
	}

	edit() {
		this.readOnly = false;
		this.buildButtons("edit");
		this.title = "Edit " + this.itemType;
		this.toastService.warn("You are now editing...");
	}

	editContactPopup(e: any) {
		this.supplierContactsService.getSingleById(e.row.data.id).subscribe(
			(res: any) => {
				this.popupVisible = true;
				this.popupAddButtonOptions = { onClick: () => this.updateContact(), text: "Save Changes" };
				this.popupCloseButtonOptions = { onClick: () => this.closePopup(), text: "Close" };
				this.popupContact = res.response;
				this.popupTitle = "Edit Contact";
				this.popupType = "Contact";
			},
			(err) => {
				console.log(err);
				this.toastService.error("Could not edit contact");
			}
		);
	}

	editLocationPopup(e: any) {
		this.supplierLocationService.getSingleById(e.row.data.id).subscribe(
			(res: any) => {
				this.popupVisible = true;
				this.popupAddButtonOptions = { onClick: () => this.updateLocation(), text: "Save Changes" };
				this.popupCloseButtonOptions = { onClick: () => this.closePopup(), text: "Close" };
				this.popupLocation = res.response;
				this.popupTitle = "Edit Location";
				this.popupType = "Location";
			},
			(err) => {
				console.log(err);
				this.toastService.error("Could not edit location");
			}
		);
	}

	formDataChanged(args: any) {
		if (args.dataField === "questionnaireCompleted" && args.value == false) {
			this.item.approvedSupplier = false;
			if (this.form?.instance) {
				this.form.instance.updateData("approvedSupplier", false);
			}
		}
	}

	getContacts() {
		this.supplierContactsService.getContactsBySupplierId(this.item.id)
			.subscribe(
				(res: any) => {
					this.contacts = res.response;
				},
				(err) => {console.log(err); this.toastService.error("Something Went Wrong!");}
			);
	}

	getContracts() {
		this.contractService.getBySupplierId(this.item.id)
			.subscribe(
				(res: any) => {
					this.contracts = res.response;
				},
				(err) => {console.log(err); this.toastService.error("Something Went Wrong!");}
			);
	}

	getDocuments() {
		this.supplierDocumentService.getDocumentsForSupplierId(this.item.id)
			.subscribe(
				(res: any) => this.documents = res.response,
				(err) => console.log(err)
			);
	}

	getItem(itemId: number) {
		this.getSelectablePurchaseTypes();
		this.itemService.getSingleById(itemId)
			.subscribe(
				(res: any) => {
					this.item = res.response;
					this.getContacts();
					this.getContracts();
					this.getDocuments();
					this.getLocations();
					this.getSupplierPurchaseTypes();
					this.getSupplierSpendSummary();
					this.getPurchaseLogs();
					this.getQuestionnaire();
				},
				(err) => {console.log(err); this.toastService.error("Something Went Wrong!");}
			);
		this.uploadEnabled = true;
	}

	getLocations() {
		this.supplierLocationService.getLocationsBySupplierId(this.item.id)
			.subscribe(
				(res: any) => this.locations = res.response,
				(err) => console.log(err)
			);
	}

	getPaymentTerms() {
		this.paymentTermsService.getAll()
			.subscribe(
				(res: any) => {
					this.paymentTerms = res.response.items;
				},
				(err) => {console.log(err); this.toastService.error("Something Went Wrong!");}
			);
	}
	
	getPurchaseLogs() {
		const today = new Date();
		const last30DaysStartDate = new Date();
		last30DaysStartDate.setDate(last30DaysStartDate.getDate() - 30);
		const next30DaysEndDate = new Date();
		next30DaysEndDate.setDate(next30DaysEndDate.getDate() + 30);
		
		this.purchaseLogService.dueBySupplierAndDateRange(this.item.id, last30DaysStartDate, today)
			.subscribe(
				(res: any) => this.purchaseLogsDueLastThirtyDays = res.response,
				(err) => console.log(err)
			);
		
		this.purchaseLogService.paymentDueBySupplierWithinDateRange(this.item.id, today, next30DaysEndDate)
			.subscribe(
				(res: any) => this.purchaseLogsPaymentDueNextThirtyDays = res.response,
				(err) => console.log(err)
			);			
		
		this.purchaseLogService.paymentOverdueBySupplier(this.item.id)
			.subscribe(
				(res: any) => this.purchaseLogsOverdue = res.response,
				(err) => console.log(err)
			);
	}

	getQuestionnaire() {
		this.supplierQuestionnaireService.forSupplier(this.itemId)
			.subscribe(
				(res: any) => {
					this.questionnaire = res.response;
					this.questionnaireApprovals = this.questionnaire == null || this.questionnaire.approvals == null ? [] : this.questionnaire.approvals;
					this.questionnaireContacts = [];
					this.questionnaireLinkUrl = this.questionnaire == null ? "" : `${window.location.origin}/public/supplierquestionaire/${this.questionnaire.lookupId}`;

					if (this.questionnaire != null) {
						try {
							this.questionnaireContacts = JSON.parse(this.questionnaire.contactsLineData || "[]");
						} catch (e) {
							this.questionnaireContacts = [];
						}
					}

					this.addResetQuestionnaireButton(); // only now do we know whether there is anything to reset
				},
				(err) => console.log(err)
			);
	}

	getSelectablePurchaseTypes() {
		this.purchaseTypes = GetPropertiesFromEnum(PurchaseTypeEnum);
		this.purchaseTypes.forEach((purchaseType: { id: PurchaseTypeEnum; name: string; }) => {
			this.selectablePurchaseTypes.push({ isSelected: false, name: purchaseType.name, value: purchaseType.id });
		});
	}

	getSupplierPurchaseTypes() {		
		this.supplierPurchaseTypeService.getPurchaseTypesForSupplierId(this.item.id)
			.subscribe(
				(res: any) => {
					this.supplierPurchaseTypes = res.response;
					this.supplierPurchaseTypes.forEach((supplierPurchaseType) => {
						const index = this.selectablePurchaseTypes.findIndex(((p: { value: PurchaseTypeEnum; }) => p.value === supplierPurchaseType.purchaseType));
						this.selectablePurchaseTypes[index].isSelected = true;
					});
				},
				(err) => console.log(err)
			);
	}

	getSupplierSpendSummary() {
		this.purchaseLogService.spendSummaryBySupplier(this.item.id)
			.subscribe(
				(res: any) => this.spendSummary = res.response,
				(err) => console.log(err)
			);
	}

	headerButtonClick(method: any) {
		// @ts-ignore // Required to be able to call the method directly from the variable
		if (this[method]) this[method]();
	}

	isTogglePurchaseTypeOffVisible(e: any) {
		return e.row.data.isSelected === true;
	}

	isTogglePurchaseTypeOnVisible(e: any) {
		return e.row.data.isSelected === false;
	}

	private questionnairePopup(mode: string) {
		if (!this.itemId) {
			this.toastService.error("Save this supplier before sending a questionnaire.");
			return;
		}

		// Default to the designated RFQ contact, else the first contact on the supplier.
		const rfqContact = this.contacts.find(c => c.id === this.item.rfqContactId);
		const defaultContact = rfqContact || this.contacts[0];
		this.questionnaireRecipientEmail = defaultContact == null ? "" : defaultContact.contactEmailAddress;

		this.questionnairePopupMode = mode;
		this.popupVisible = true;
		this.popupTitle = mode === "reset" ? "Reset & Re-send Supplier Questionnaire" : "Send Supplier Questionnaire";
		this.popupType = "Questionnaire";
		this.popupAddButtonOptions = mode === "reset"
			? { onClick: () => this.resetQuestionnaire(), text: "Reset & Re-send" }
			: { onClick: () => this.sendQuestionnaire(), text: "Send Questionnaire" };
	}

	/**
	 * Re-issues the questionnaire AND clears the supplier's Questionnaire Completed / Approved Supplier
	 * flags, so they have to complete and be approved again. The previous questionnaire and its approvals
	 * stay as the record of what was approved before.
	 */
	resetQuestionnaire() {
		if (!this.questionnaireRecipientEmail) {
			this.toastService.error("Please choose or enter a contact email address.");
			return;
		}

		if (confirm("This will clear this supplier's Questionnaire Completed and Approved Supplier status and send them a new questionnaire. They will need to complete it and be approved by two people again. Continue?") === false) {
			return;
		}

		this.supplierQuestionnaireService.reset(this.itemId, this.questionnaireRecipientEmail)
			.subscribe(
				() => {
					this.closePopup();
					this.toastService.success("Supplier reset and a new questionnaire sent to " + this.questionnaireRecipientEmail);
					this.getQuestionnaire();
					this.getItem(this.itemId); // the approval flags have just been cleared
				},
				(err: any) => {
					console.log(err);
					this.toastService.error(err?.error?.response?.error || "Could not reset the questionnaire.");
				}
			);
	}

	resetQuestionnairePopup() {
		this.questionnairePopup("reset");
	}

	sendQuestionnaire() {
		if (!this.questionnaireRecipientEmail) {
			this.toastService.error("Please choose or enter a contact email address.");
			return;
		}

		this.supplierQuestionnaireService.send(this.itemId, this.questionnaireRecipientEmail)
			.subscribe(
				() => {
					this.closePopup();
					this.toastService.success("Questionnaire sent to " + this.questionnaireRecipientEmail);
					this.getQuestionnaire();
				},
				(err: any) => {
					console.log(err);
					this.toastService.error(err?.error?.response?.error || "Could not send the questionnaire.");
				}
			);
	}

	sendQuestionnairePopup() {
		this.questionnairePopup("send");
	}

	setPrimaryBilling(e: any) {
		this.supplierLocationService.setPrimaryBilling(e.row.data.id).subscribe(
			() => {
				this.toastService.success("Successfully Updated Primary Billing Location");
				this.getLocations();
			},
			(err) => {
				console.log(err);
				this.toastService.error("Could not edit location");
			}
		);
	}

	setPrimaryDelivery(e: any) {
		this.supplierLocationService.setPrimaryDelivery(e.row.data.id).subscribe(
			() => {
				this.toastService.success("Successfully Updated Primary Delivery Location");
				this.getLocations();
			},
			(err) => {
				console.log(err);
				this.toastService.error("Could not edit location");
			}
		);
	}

	togglePurchaseType(e: any) {
		this.supplierPurchaseTypeService.togglePurchaseType(this.item.id, e.row.data.value)
			.subscribe(
				() => {
					const index = this.selectablePurchaseTypes.findIndex((p => p.value === e.row.data.value));
					this.selectablePurchaseTypes[index].isSelected = !this.selectablePurchaseTypes[index].isSelected;
				},
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				}
			);
	}

	updateContact() {
		this.supplierContactsService.updateSingleById(this.popupContact.id, this.popupContact)
			.subscribe(
				() => {
					this.toastService.success("Successfully Updated Contact");					
					this.closePopup();
					this.getContacts();
				},
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				}
			);
	}

	updateItem() {
		this.itemService.updateSingleById(this.item.id, this.item)
			.subscribe(
				() => this.toastService.success("Successfully Updated "),
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				}
			);
	}

	updateLocation() {
		this.supplierLocationService.updateSingleById(this.popupLocation.id, this.popupLocation)
			.subscribe(
				() => {
					this.toastService.success("Successfully Updated Location");				
					this.closePopup();
					this.getLocations();
				},
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				}
			);
	}

	uploadFile(file: File) {
		const uploadData = new FormData();
		uploadData.append("files", file);
		this.fileService.uploadFile(uploadData)
			.subscribe(
				(res: any) => {
					this.popupSupplierDocument.fileLookupId = res.response[0].lookupId;
					this.popupSupplierDocument.fileName = res.response[0].name;
				},
				(err) => console.log(err)
			);
	}

	viewContractLink(e: any) {
		this.router.navigate([`/admin/contracts/${e.row.data.lookupId}`]);
	}

	viewPurchaseLogLink(e: any) {
		this.router.navigate([`/admin/purchaseLogs/${e.row.data.lookupId}`]);
	}
}